import type {Dispatch, ReactNode} from 'react';
import {
  createContext,
  useContext,
  useId,
  useMemo,
  useEffect,
  useReducer,
  useRef,
} from 'react';
import {Theme} from '@wellstone-solutions/common';
import {
  Box,
  Card,
  CardContent,
  Icon,
  IconNames,
  Stack,
  Typography,
} from '@wellstone-solutions/web';
import {evaluateProgress} from './progress';
import type {NoteDefinition, NoteProgress} from './types';

type NoteFormContextValue<S, A> = {
  definition: NoteDefinition<S, A>;
  state: S;
  dispatch: Dispatch<A>;
  progress: NoteProgress;
  isSubmitting: boolean;
};

const NoteFormContext = createContext<NoteFormContextValue<
  unknown,
  unknown
> | null>(null);

export const useNoteForm = <S, A>(): NoteFormContextValue<S, A> => {
  const context = useContext(NoteFormContext);
  if (!context) {
    throw new Error('NoteForm parts must be rendered inside <NoteForm>');
  }
  // Root provides the definition's own S and A; context can't carry generics
  return context as unknown as NoteFormContextValue<S, A>;
};

type RootProps<S, A> = {
  definition: NoteDefinition<S, A>;
  isSubmitting?: boolean;
  children: ReactNode;
};

const Root = <S, A>({
  definition,
  isSubmitting = false,
  children,
}: RootProps<S, A>) => {
  const [state, dispatch] = useReducer(
    definition.reducer,
    undefined,
    definition.createInitialState,
  );

  const progress = useMemo(
    () => evaluateProgress(definition, state),
    [definition, state],
  );

  const value = useMemo(
    () => ({definition, state, dispatch, progress, isSubmitting}),
    [definition, state, progress, isSubmitting],
  );

  return (
    <NoteFormContext.Provider
      value={value as unknown as NoteFormContextValue<unknown, unknown>}>
      {children}
    </NoteFormContext.Provider>
  );
};

const requiredColor = Theme.colorPalette.warning;

export const requiredLabelSx = {
  '& .MuiFormLabel-asterisk': {color: requiredColor, fontWeight: 700},
};

export const halfWidthSx = {width: {xs: '100%', md: '50%'}};

// For web inputs that don't forward `required` (AutocompleteSearch, DatePicker)
export const markLabelRequiredSx = {
  '& .MuiInputLabel-root::after': {
    content: '" *"',
    color: requiredColor,
    fontWeight: 700,
  },
};

export const RequiredMark = () => (
  <Box
    component="span"
    aria-hidden
    sx={{color: requiredColor, fontWeight: 700, ml: 0.5}}>
    *
  </Box>
);

export const visuallyHiddenSx = {
  position: 'absolute',
  // sx reads bare 1 as 100%
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;

const Layout = ({children}: {children: ReactNode}) => {
  const {isSubmitting} = useNoteForm();
  const layoutRef = useRef<HTMLDivElement>(null);
  const focusBeforeLock = useRef<HTMLElement | null>(null);

  // inert blocks clicks, typing and focus; React 18 has no prop for it
  useEffect(() => {
    if (isSubmitting) {
      focusBeforeLock.current = document.activeElement as HTMLElement | null;
      layoutRef.current?.setAttribute('inert', '');
      return;
    }
    layoutRef.current?.removeAttribute('inert');
    // Browsers drop focus from an inert subtree, so hand it back after a failed submit
    if (focusBeforeLock.current?.isConnected) {
      focusBeforeLock.current.focus();
    }
    focusBeforeLock.current = null;
  }, [isSubmitting]);

  return (
    <>
      <Box role="status" sx={visuallyHiddenSx}>
        {isSubmitting ? 'Saving note…' : ''}
      </Box>
      <Box sx={requiredLabelSx}>
        <div
          ref={layoutRef}
          style={{
            opacity: isSubmitting ? 0.6 : 1,
            transition: 'opacity 150ms',
          }}>
          {children}
        </div>
      </Box>
    </>
  );
};

const Sections = ({children}: {children: ReactNode}) => (
  <Stack spacing={2}>{children}</Stack>
);

const SectionStatus = ({complete}: {complete: boolean}) =>
  complete ? (
    <Box
      component="span"
      role="img"
      aria-label="Complete"
      sx={{display: 'inline-flex'}}>
      <Icon name={IconNames.CheckCircle} color={Theme.colorPalette.positive} />
    </Box>
  ) : (
    <Box
      component="span"
      role="img"
      aria-label="Incomplete"
      sx={{color: requiredColor, fontWeight: 700, fontSize: 20}}>
      *
    </Box>
  );

const NumberBadge = ({number}: {number: number}) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 28,
      height: 28,
      borderRadius: '50%',
      bgcolor: Theme.colorPalette.primary4,
      color: Theme.colorPalette.primary,
      fontWeight: 700,
      fontSize: 14,
    }}>
    {number}
  </Box>
);

type SectionProps = {
  id: string;
  title: string;
  number?: number;
  children: ReactNode;
};

const Section = ({id, title, number, children}: SectionProps) => {
  const {progress} = useNoteForm();
  const titleId = useId();
  const section = progress.sections[id];

  if (!section) {
    throw new Error(`Unknown NoteForm section "${id}"`);
  }
  if (!section.visible) {
    return null;
  }

  return (
    <Card component="section" aria-labelledby={titleId} variant="outlined">
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
          {number !== undefined && <NumberBadge number={number} />}
          <Typography id={titleId} variant="h6">
            {title}
          </Typography>
          <Box sx={{ml: 'auto !important'}}>
            <SectionStatus complete={section.complete} />
          </Box>
        </Stack>
        {children}
      </CardContent>
    </Card>
  );
};

export const NoteForm = Object.assign(Root, {
  Layout,
  Sections,
  Section,
});
